import React, { useState, useEffect } from 'react';
import {
  Search, Plus, Users, Calendar, DollarSign, CheckCircle, Clock,
  XCircle, ChevronRight, Play, FileText, Building, UserPlus, AlertTriangle
} from 'lucide-react';
import {
  getTeamMembers, getTeamSummary, getTeamMember, createTeamMember, updateTeamMember,
  getPayrollRuns, getPayrollRun, createPayrollRun, approvePayrollRun, processPayrollRun, cancelPayrollRun,
  updatePayrollItem
} from '../../services/adminDealFlowApi';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/new-components/ui/card';
import { Button } from '../../components/new-components/ui/button';
import { Badge } from '../../components/new-components/ui/badge';
import { Input } from '../../components/new-components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../../components/new-components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/new-components/ui/select';

const formatCurrency = (cents: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);

const unwrap = (res: any) => {
  const body = res?.data?.success !== undefined ? res.data : (res?.success !== undefined ? res : res?.data || res);
  return body;
};

const statusColor = (s: string): 'default' | 'secondary' | 'destructive' | 'outline' => {
  switch (s) {
    case 'active': case 'completed': case 'settled': return 'default';
    case 'approved': case 'processing': return 'secondary';
    case 'inactive': case 'terminated': case 'cancelled': case 'failed': case 'returned': return 'destructive';
    default: return 'outline';
  }
};

type View = 'team' | 'runs' | 'member-detail' | 'run-detail';

const Payroll: React.FC = () => {
  const [view, setView] = useState<View>('team');
  const [team, setTeam] = useState<any[]>([]);
  const [runs, setRuns] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [selected, setSelected] = useState<any>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  // Dialogs
  const [showAddMember, setShowAddMember] = useState(false);
  const [showNewRun, setShowNewRun] = useState(false);

  // Form state
  const [memberForm, setMemberForm] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    ssn: '', date_of_birth: '',
    address_line1: '', city: '', state: '', zip: '',
    employment_type: 'w2' as 'w2' | '1099',
    job_title: '', department: '', hire_date: '',
    pay_rate: '', pay_frequency: 'biweekly', annual_salary: '',
    bank_name: '', ach_routing: '', ach_account_number: '', ach_account_type: 'checking',
  });
  const [runForm, setRunForm] = useState({
    pay_period_start: '', pay_period_end: '', pay_date: '', notes: '',
  });

  // ── Data loading ──────────────────────────────────────────
  const loadTeam = async () => {
    setLoading(true);
    try {
      const res = unwrap(await getTeamMembers({
        search: search || undefined, status: statusFilter || undefined, page,
      }));
      setTeam(res.data || []);
      setPagination(res.pagination);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const loadSummary = async () => {
    try {
      const res = unwrap(await getTeamSummary());
      setSummary(res.data);
    } catch (e) { console.error(e); }
  };

  const loadRuns = async () => {
    setLoading(true);
    try {
      const res = unwrap(await getPayrollRuns({ status: statusFilter || undefined, page }));
      setRuns(res.data || []);
      setPagination(res.pagination);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const loadMemberDetail = async (id: string) => {
    try {
      const res = unwrap(await getTeamMember(id));
      setSelected(res.data);
      setView('member-detail');
    } catch (e) { console.error(e); }
  };

  const loadRunDetail = async (id: string) => {
    try {
      const res = unwrap(await getPayrollRun(id));
      setSelected(res.data);
      setView('run-detail');
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    if (view === 'team') { loadTeam(); loadSummary(); }
    if (view === 'runs') loadRuns();
  }, [view, page, statusFilter]);

  useEffect(() => {
    const t = setTimeout(() => { if (view === 'team') loadTeam(); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  // ── Actions ───────────────────────────────────────────────
  const handleCreateMember = async () => {
    try {
      await createTeamMember({
        first_name: memberForm.first_name,
        last_name: memberForm.last_name,
        email: memberForm.email || undefined,
        phone: memberForm.phone || undefined,
        ssn: memberForm.ssn || undefined,
        date_of_birth: memberForm.date_of_birth || undefined,
        address_line1: memberForm.address_line1 || undefined,
        city: memberForm.city || undefined,
        state: memberForm.state || undefined,
        zip: memberForm.zip || undefined,
        employment_type: memberForm.employment_type,
        job_title: memberForm.job_title || undefined,
        department: memberForm.department || undefined,
        hire_date: memberForm.hire_date || undefined,
        pay_rate_cents: Math.round(Number(memberForm.pay_rate) * 100),
        pay_frequency: memberForm.pay_frequency as any,
        annual_salary_cents: memberForm.annual_salary ? Math.round(Number(memberForm.annual_salary) * 100) : undefined,
        bank_name: memberForm.bank_name || undefined,
        ach_routing: memberForm.ach_routing || undefined,
        ach_account_number: memberForm.ach_account_number || undefined,
        ach_account_type: memberForm.ach_account_type as any,
      });
      setShowAddMember(false);
      setMemberForm({
        first_name: '', last_name: '', email: '', phone: '',
        ssn: '', date_of_birth: '',
        address_line1: '', city: '', state: '', zip: '',
        employment_type: 'w2', job_title: '', department: '', hire_date: '',
        pay_rate: '', pay_frequency: 'biweekly', annual_salary: '',
        bank_name: '', ach_routing: '', ach_account_number: '', ach_account_type: 'checking',
      });
      loadTeam();
      loadSummary();
    } catch (e) { console.error(e); }
  };

  const handleCreateRun = async () => {
    try {
      const res = unwrap(await createPayrollRun({
        pay_period_start: runForm.pay_period_start,
        pay_period_end: runForm.pay_period_end,
        pay_date: runForm.pay_date,
        notes: runForm.notes || undefined,
      }));
      setShowNewRun(false);
      setRunForm({ pay_period_start: '', pay_period_end: '', pay_date: '', notes: '' });
      setSelected(res.data);
      setView('run-detail');
    } catch (e) { console.error(e); }
  };

  const handleApproveRun = async (id: string) => {
    try { await approvePayrollRun(id); loadRunDetail(id); } catch (e) { console.error(e); }
  };

  const handleProcessRun = async (id: string) => {
    if (!window.confirm('Process this payroll? ACH direct deposits will be initiated for all team members.')) return;
    try {
      const res = unwrap(await processPayrollRun(id));
      alert(res.message || 'Payroll processed');
      loadRunDetail(id);
    } catch (e) { console.error(e); }
  };

  const handleCancelRun = async (id: string) => {
    if (!window.confirm('Cancel this payroll run?')) return;
    try { await cancelPayrollRun(id); loadRuns(); setView('runs'); } catch (e) { console.error(e); }
  };

  // ── Render ────────────────────────────────────────────────
  return (
    <div className="space-y-5 sm:space-y-6 min-w-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold">Payroll</h1>
          <p className="text-sm text-muted-foreground">Team management and direct deposit payroll</p>
        </div>
        <div className="grid grid-cols-2 sm:flex gap-2 w-full sm:w-auto">
          <Button variant={view === 'team' ? 'default' : 'outline'} size="sm" onClick={() => { setView('team'); setPage(1); setStatusFilter(''); }}>
            <Users className="h-4 w-4 mr-1" /> Team
          </Button>
          <Button variant={view === 'runs' || view === 'run-detail' ? 'default' : 'outline'} size="sm" onClick={() => { setView('runs'); setPage(1); setStatusFilter(''); }}>
            <Calendar className="h-4 w-4 mr-1" /> Payroll Runs
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && view !== 'run-detail' && view !== 'member-detail' && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="text-sm text-muted-foreground">Active Team</div>
              <div className="text-2xl font-bold">{Number(summary.active_members || 0)}</div>
              <div className="text-xs text-muted-foreground">
                {Number(summary.w2_count || 0)} W-2 · {Number(summary.contractor_count || 0)} 1099
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="text-sm text-muted-foreground">Draft Runs</div>
              <div className="text-2xl font-bold">{Number(summary.draft_runs || 0)}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="text-sm text-muted-foreground">Processing</div>
              <div className="text-2xl font-bold text-amber-600">{Number(summary.processing_runs || 0)}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="text-sm text-muted-foreground">Total Paid</div>
              <div className="text-2xl font-bold">{formatCurrency(Number(summary.total_paid_cents || 0))}</div>
              <div className="text-xs text-muted-foreground">{Number(summary.completed_runs || 0)} completed runs</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Toolbar */}
      {(view === 'team' || view === 'runs') && (
        <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_auto] gap-2 sm:items-center">
          {view === 'team' && (
            <div className="relative min-w-0 sm:max-w-sm">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search team members..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} className="pl-9" />
            </div>
          )}
          <Select value={statusFilter} onValueChange={v => { setStatusFilter(v === 'all' ? '' : v); setPage(1); }}>
            <SelectTrigger className="w-full sm:w-[140px]"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {view === 'team'
                ? <>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="terminated">Terminated</SelectItem>
                  </>
                : <>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="processing">Processing</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </>
              }
            </SelectContent>
          </Select>
          {view === 'team' && (
            <Button onClick={() => setShowAddMember(true)}><UserPlus className="h-4 w-4 mr-1" /> Add Member</Button>
          )}
          {view === 'runs' && (
            <Button onClick={() => setShowNewRun(true)}><Plus className="h-4 w-4 mr-1" /> New Payroll Run</Button>
          )}
        </div>
      )}

      {/* ── TEAM LIST ──────────────────────────────────────── */}
      {view === 'team' && (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm min-w-[500px]">
              <thead>
                <tr className="border-b bg-muted/30">
                  <th className="text-left p-3 font-medium">Name</th>
                  <th className="text-left p-3 font-medium hidden md:table-cell">Title</th>
                  <th className="text-left p-3 font-medium hidden md:table-cell">Type</th>
                  <th className="text-right p-3 font-medium">Pay Rate</th>
                  <th className="text-left p-3 font-medium hidden md:table-cell">Frequency</th>
                  <th className="text-left p-3 font-medium hidden md:table-cell">Bank</th>
                  <th className="text-left p-3 font-medium">Status</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {team.map(m => (
                  <tr key={m.id} className="border-b hover:bg-muted/20 cursor-pointer" onClick={() => loadMemberDetail(m.id)}>
                    <td className="p-3">
                      <div className="font-medium">{m.first_name} {m.last_name}</div>
                      <div className="text-xs text-muted-foreground">{m.email || m.member_id}</div>
                    </td>
                    <td className="p-3 hidden md:table-cell">{m.job_title || '—'}</td>
                    <td className="p-3 hidden md:table-cell">
                      <Badge variant="outline">{m.employment_type === 'w2' ? 'W-2' : '1099'}</Badge>
                    </td>
                    <td className="p-3 text-right font-mono">{formatCurrency(Number(m.pay_rate_cents))}</td>
                    <td className="p-3 hidden md:table-cell capitalize">{(m.pay_frequency || 'biweekly').replace('_', ' ')}</td>
                    <td className="p-3 hidden md:table-cell">
                      {m.ach_account_last4 ? `****${m.ach_account_last4}` : <span className="text-amber-500 text-xs">No bank</span>}
                    </td>
                    <td className="p-3"><Badge variant={statusColor(m.status)}>{m.status}</Badge></td>
                    <td className="p-3"><ChevronRight className="h-4 w-4 text-muted-foreground" /></td>
                  </tr>
                ))}
                {!team.length && !loading && (
                  <tr><td colSpan={8} className="p-8 text-center text-muted-foreground">No team members found</td></tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* ── PAYROLL RUNS LIST ──────────────────────────────── */}
      {view === 'runs' && (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm min-w-[500px]">
              <thead>
                <tr className="border-b bg-muted/30">
                  <th className="text-left p-3 font-medium">Run ID</th>
                  <th className="text-left p-3 font-medium">Pay Period</th>
                  <th className="text-left p-3 font-medium">Pay Date</th>
                  <th className="text-right p-3 font-medium">Employees</th>
                  <th className="text-right p-3 font-medium">Total Net</th>
                  <th className="text-left p-3 font-medium">Status</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {runs.map(r => (
                  <tr key={r.id} className="border-b hover:bg-muted/20 cursor-pointer" onClick={() => loadRunDetail(r.id)}>
                    <td className="p-3 font-medium">{r.run_id}</td>
                    <td className="p-3">
                      {r.pay_period_start && new Date(r.pay_period_start).toLocaleDateString()} –{' '}
                      {r.pay_period_end && new Date(r.pay_period_end).toLocaleDateString()}
                    </td>
                    <td className="p-3">{r.pay_date && new Date(r.pay_date).toLocaleDateString()}</td>
                    <td className="p-3 text-right">{r.employee_count}</td>
                    <td className="p-3 text-right font-mono font-medium">{formatCurrency(Number(r.total_net_cents || 0))}</td>
                    <td className="p-3"><Badge variant={statusColor(r.status)}>{r.status}</Badge></td>
                    <td className="p-3"><ChevronRight className="h-4 w-4 text-muted-foreground" /></td>
                  </tr>
                ))}
                {!runs.length && !loading && (
                  <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">No payroll runs found</td></tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* ── MEMBER DETAIL ──────────────────────────────────── */}
      {view === 'member-detail' && selected && (
        <div className="space-y-4">
          <Button variant="outline" size="sm" onClick={() => { setView('team'); setSelected(null); }}>
            ← Back to Team
          </Button>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  {selected.first_name} {selected.last_name}
                  <Badge variant={statusColor(selected.status)} className="ml-2">{selected.status}</Badge>
                  <Badge variant="outline" className="ml-1">{selected.employment_type === 'w2' ? 'W-2' : '1099'}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-muted-foreground">ID:</span> {selected.member_id}</div>
                <div><span className="text-muted-foreground">Email:</span> {selected.email || '—'}</div>
                <div><span className="text-muted-foreground">Phone:</span> {selected.phone || '—'}</div>
                <div><span className="text-muted-foreground">Title:</span> {selected.job_title || '—'}</div>
                <div><span className="text-muted-foreground">Department:</span> {selected.department || '—'}</div>
                <div><span className="text-muted-foreground">Hire Date:</span> {selected.hire_date ? new Date(selected.hire_date).toLocaleDateString() : '—'}</div>
                <div><span className="text-muted-foreground">Pay Rate:</span> {formatCurrency(Number(selected.pay_rate_cents))} / {selected.pay_frequency}</div>
                {selected.annual_salary_cents && <div><span className="text-muted-foreground">Annual:</span> {formatCurrency(Number(selected.annual_salary_cents))}</div>}
                <div><span className="text-muted-foreground">SSN:</span> {selected.ssn_last4 ? `***-**-${selected.ssn_last4}` : '—'}</div>
                <div><span className="text-muted-foreground">Bank:</span> {selected.bank_name || '—'} {selected.ach_account_last4 ? `(****${selected.ach_account_last4})` : ''}</div>
                {selected.address_line1 && (
                  <div className="col-span-2">
                    <span className="text-muted-foreground">Address:</span>{' '}
                    {selected.address_line1}{selected.city ? `, ${selected.city}` : ''}{selected.state ? `, ${selected.state}` : ''} {selected.zip}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Hiring Documents */}
            <Card>
              <CardHeader><CardTitle className="text-sm">Hiring Documents</CardTitle></CardHeader>
              <CardContent>
                {(selected.documents || []).length > 0 ? (
                  <ul className="space-y-2 text-sm">
                    {(selected.documents || []).map((doc: any, i: number) => (
                      <li key={i} className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        <span>{doc.name || doc.type}</span>
                        {doc.verified && <CheckCircle className="h-3.5 w-3.5 text-green-500" />}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-muted-foreground">No documents uploaded</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Pay History */}
          <Card>
            <CardHeader><CardTitle className="text-sm">Pay History</CardTitle></CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm min-w-[400px]">
                <thead>
                  <tr className="border-b bg-muted/30">
                    <th className="text-left p-3 font-medium">Run</th>
                    <th className="text-left p-3 font-medium">Period</th>
                    <th className="text-right p-3 font-medium">Gross</th>
                    <th className="text-right p-3 font-medium">Deductions</th>
                    <th className="text-right p-3 font-medium">Net Pay</th>
                    <th className="text-left p-3 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(selected.pay_history || []).map((ph: any) => (
                    <tr key={ph.id} className="border-b">
                      <td className="p-3 font-medium">{ph.run_id}</td>
                      <td className="p-3">{new Date(ph.pay_period_start).toLocaleDateString()} – {new Date(ph.pay_period_end).toLocaleDateString()}</td>
                      <td className="p-3 text-right font-mono">{formatCurrency(Number(ph.gross_pay_cents))}</td>
                      <td className="p-3 text-right font-mono">{formatCurrency(Number(ph.deductions_cents || 0))}</td>
                      <td className="p-3 text-right font-mono font-medium">{formatCurrency(Number(ph.net_pay_cents))}</td>
                      <td className="p-3"><Badge variant={statusColor(ph.status)}>{ph.status}</Badge></td>
                    </tr>
                  ))}
                  {!(selected.pay_history || []).length && (
                    <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">No pay history yet</td></tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ── RUN DETAIL ─────────────────────────────────────── */}
      {view === 'run-detail' && selected && (
        <div className="space-y-4">
          <Button variant="outline" size="sm" onClick={() => { setView('runs'); setSelected(null); }}>
            ← Back to Payroll Runs
          </Button>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="md:col-span-3">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  {selected.run_id}
                  <Badge variant={statusColor(selected.status)} className="ml-2">{selected.status}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Pay Period</span>
                  <div>{new Date(selected.pay_period_start).toLocaleDateString()} – {new Date(selected.pay_period_end).toLocaleDateString()}</div>
                </div>
                <div>
                  <span className="text-muted-foreground">Pay Date</span>
                  <div>{new Date(selected.pay_date).toLocaleDateString()}</div>
                </div>
                <div>
                  <span className="text-muted-foreground">Employees</span>
                  <div className="font-medium">{selected.employee_count}</div>
                </div>
                <div>
                  <span className="text-muted-foreground">Total Net</span>
                  <div className="font-bold text-lg">{formatCurrency(Number(selected.total_net_cents || 0))}</div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-sm">Actions</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {selected.status === 'draft' && (
                  <>
                    <Button className="w-full" size="sm" onClick={() => handleApproveRun(selected.id)}>
                      <CheckCircle className="h-4 w-4 mr-1" /> Approve
                    </Button>
                    <Button variant="destructive" className="w-full" size="sm" onClick={() => handleCancelRun(selected.id)}>
                      <XCircle className="h-4 w-4 mr-1" /> Cancel
                    </Button>
                  </>
                )}
                {selected.status === 'approved' && (
                  <>
                    <Button className="w-full" size="sm" onClick={() => handleProcessRun(selected.id)}>
                      <Play className="h-4 w-4 mr-1" /> Process Payroll
                    </Button>
                    <Button variant="destructive" className="w-full" size="sm" onClick={() => handleCancelRun(selected.id)}>
                      <XCircle className="h-4 w-4 mr-1" /> Cancel
                    </Button>
                  </>
                )}
                {selected.status === 'processing' && (
                  <div className="text-sm text-amber-600 flex items-center gap-1">
                    <Clock className="h-4 w-4" /> ACH transfers in progress
                  </div>
                )}
                {selected.status === 'completed' && (
                  <div className="text-sm text-green-600 flex items-center gap-1">
                    <CheckCircle className="h-4 w-4" /> All deposits settled
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Payroll Items */}
          <Card>
            <CardHeader><CardTitle className="text-sm">Payroll Items</CardTitle></CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm min-w-[500px]">
                <thead>
                  <tr className="border-b bg-muted/30">
                    <th className="text-left p-3 font-medium">Employee</th>
                    <th className="text-left p-3 font-medium hidden md:table-cell">Type</th>
                    <th className="text-right p-3 font-medium">Gross Pay</th>
                    <th className="text-right p-3 font-medium">Deductions</th>
                    <th className="text-right p-3 font-medium">Net Pay</th>
                    <th className="text-left p-3 font-medium hidden md:table-cell">Account</th>
                    <th className="text-left p-3 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(selected.items || []).map((item: any) => (
                    <tr key={item.id} className="border-b">
                      <td className="p-3">
                        <div className="font-medium">{item.first_name} {item.last_name}</div>
                        <div className="text-xs text-muted-foreground">{item.email || item.member_id}</div>
                      </td>
                      <td className="p-3 hidden md:table-cell">{item.employment_type === 'w2' ? 'W-2' : '1099'}</td>
                      <td className="p-3 text-right font-mono">{formatCurrency(Number(item.gross_pay_cents))}</td>
                      <td className="p-3 text-right font-mono">{formatCurrency(Number(item.deductions_cents || 0))}</td>
                      <td className="p-3 text-right font-mono font-medium">{formatCurrency(Number(item.net_pay_cents))}</td>
                      <td className="p-3 hidden md:table-cell">
                        {item.ach_account_last4 ? `****${item.ach_account_last4}` : (
                          <span className="text-amber-500 flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3" /> No bank
                          </span>
                        )}
                      </td>
                      <td className="p-3"><Badge variant={statusColor(item.status)}>{item.status}</Badge></td>
                    </tr>
                  ))}
                  {!(selected.items || []).length && (
                    <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">No items in this payroll run</td></tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>

          {selected.notes && (
            <Card>
              <CardContent className="pt-4 text-sm">
                <span className="text-muted-foreground">Notes:</span> {selected.notes}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.pages > 1 && (view === 'team' || view === 'runs') && (
        <div className="flex justify-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
          <span className="text-sm py-2 px-3">Page {page} of {pagination.pages}</span>
          <Button variant="outline" size="sm" disabled={page >= pagination.pages} onClick={() => setPage(p => p + 1)}>Next</Button>
        </div>
      )}

      {/* ── ADD MEMBER DIALOG ──────────────────────────────── */}
      <Dialog open={showAddMember} onOpenChange={setShowAddMember}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Add Team Member</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">First Name *</label>
              <Input value={memberForm.first_name} onChange={e => setMemberForm({ ...memberForm, first_name: e.target.value })} />
            </div>
            <div>
              <label className="text-sm font-medium">Last Name *</label>
              <Input value={memberForm.last_name} onChange={e => setMemberForm({ ...memberForm, last_name: e.target.value })} />
            </div>
            <div>
              <label className="text-sm font-medium">Email</label>
              <Input type="email" value={memberForm.email} onChange={e => setMemberForm({ ...memberForm, email: e.target.value })} />
            </div>
            <div>
              <label className="text-sm font-medium">Phone</label>
              <Input value={memberForm.phone} onChange={e => setMemberForm({ ...memberForm, phone: e.target.value })} />
            </div>

            <div className="col-span-2 border-t pt-3 mt-1">
              <h4 className="font-medium text-sm mb-2">Employment</h4>
            </div>
            <div>
              <label className="text-sm font-medium">Employment Type</label>
              <Select value={memberForm.employment_type} onValueChange={v => setMemberForm({ ...memberForm, employment_type: v as 'w2' | '1099' })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="w2">W-2 Employee</SelectItem>
                  <SelectItem value="1099">1099 Contractor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Job Title</label>
              <Input value={memberForm.job_title} onChange={e => setMemberForm({ ...memberForm, job_title: e.target.value })} />
            </div>
            <div>
              <label className="text-sm font-medium">Department</label>
              <Input value={memberForm.department} onChange={e => setMemberForm({ ...memberForm, department: e.target.value })} />
            </div>
            <div>
              <label className="text-sm font-medium">Hire Date</label>
              <Input type="date" value={memberForm.hire_date} onChange={e => setMemberForm({ ...memberForm, hire_date: e.target.value })} />
            </div>

            <div className="col-span-2 border-t pt-3 mt-1">
              <h4 className="font-medium text-sm mb-2">Compensation</h4>
            </div>
            <div>
              <label className="text-sm font-medium">Pay Rate ($ per period) *</label>
              <Input type="number" step="0.01" min="0" value={memberForm.pay_rate} onChange={e => setMemberForm({ ...memberForm, pay_rate: e.target.value })} />
            </div>
            <div>
              <label className="text-sm font-medium">Pay Frequency</label>
              <Select value={memberForm.pay_frequency} onValueChange={v => setMemberForm({ ...memberForm, pay_frequency: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="biweekly">Biweekly</SelectItem>
                  <SelectItem value="semi_monthly">Semi-Monthly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Annual Salary ($)</label>
              <Input type="number" step="0.01" min="0" value={memberForm.annual_salary}
                onChange={e => setMemberForm({ ...memberForm, annual_salary: e.target.value })} placeholder="Optional" />
            </div>

            <div className="col-span-2 border-t pt-3 mt-1">
              <h4 className="font-medium text-sm mb-2">Personal / Tax</h4>
            </div>
            <div>
              <label className="text-sm font-medium">SSN</label>
              <Input value={memberForm.ssn} onChange={e => setMemberForm({ ...memberForm, ssn: e.target.value })} placeholder="XXX-XX-XXXX" />
            </div>
            <div>
              <label className="text-sm font-medium">Date of Birth</label>
              <Input type="date" value={memberForm.date_of_birth} onChange={e => setMemberForm({ ...memberForm, date_of_birth: e.target.value })} />
            </div>

            <div className="col-span-2 border-t pt-3 mt-1">
              <h4 className="font-medium text-sm mb-2">Address</h4>
            </div>
            <div className="col-span-2">
              <Input placeholder="Street address" value={memberForm.address_line1} onChange={e => setMemberForm({ ...memberForm, address_line1: e.target.value })} />
            </div>
            <div>
              <Input placeholder="City" value={memberForm.city} onChange={e => setMemberForm({ ...memberForm, city: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="State" value={memberForm.state} onChange={e => setMemberForm({ ...memberForm, state: e.target.value })} />
              <Input placeholder="ZIP" value={memberForm.zip} onChange={e => setMemberForm({ ...memberForm, zip: e.target.value })} />
            </div>

            <div className="col-span-2 border-t pt-3 mt-1">
              <h4 className="font-medium text-sm mb-2">Direct Deposit (ACH)</h4>
            </div>
            <div>
              <label className="text-sm font-medium">Bank Name</label>
              <Input value={memberForm.bank_name} onChange={e => setMemberForm({ ...memberForm, bank_name: e.target.value })} />
            </div>
            <div>
              <label className="text-sm font-medium">Account Type</label>
              <Select value={memberForm.ach_account_type} onValueChange={v => setMemberForm({ ...memberForm, ach_account_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="checking">Checking</SelectItem>
                  <SelectItem value="savings">Savings</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Routing Number</label>
              <Input value={memberForm.ach_routing} onChange={e => setMemberForm({ ...memberForm, ach_routing: e.target.value })} placeholder="9 digits" />
            </div>
            <div>
              <label className="text-sm font-medium">Account Number</label>
              <Input value={memberForm.ach_account_number} onChange={e => setMemberForm({ ...memberForm, ach_account_number: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddMember(false)}>Cancel</Button>
            <Button onClick={handleCreateMember} disabled={!memberForm.first_name || !memberForm.last_name || !memberForm.pay_rate}>
              Add Team Member
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── NEW PAYROLL RUN DIALOG ─────────────────────────── */}
      <Dialog open={showNewRun} onOpenChange={setShowNewRun}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>New Payroll Run</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Pay Period Start *</label>
              <Input type="date" value={runForm.pay_period_start} onChange={e => setRunForm({ ...runForm, pay_period_start: e.target.value })} />
            </div>
            <div>
              <label className="text-sm font-medium">Pay Period End *</label>
              <Input type="date" value={runForm.pay_period_end} onChange={e => setRunForm({ ...runForm, pay_period_end: e.target.value })} />
            </div>
            <div>
              <label className="text-sm font-medium">Pay Date *</label>
              <Input type="date" value={runForm.pay_date} onChange={e => setRunForm({ ...runForm, pay_date: e.target.value })} />
            </div>
            <div>
              <label className="text-sm font-medium">Notes</label>
              <textarea className="w-full rounded-md border p-2 text-sm" rows={2} value={runForm.notes}
                onChange={e => setRunForm({ ...runForm, notes: e.target.value })} />
            </div>
            <p className="text-xs text-muted-foreground">
              All active team members will be included. Pay amounts default to each member's configured pay rate.
              You can adjust individual amounts after creation.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewRun(false)}>Cancel</Button>
            <Button onClick={handleCreateRun} disabled={!runForm.pay_period_start || !runForm.pay_period_end || !runForm.pay_date}>
              Create Payroll Run
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Payroll;
